"use client";

/**
 * The band the panel becomes while the trace is being read.
 *
 * The trace is a mode, not a layer (see `useTrace`): it cannot be read while
 * anything moves, and the roll it is drawn on is the screen. So the panel
 * gives up everything it was offering and holds one sentence and one way out.
 *
 * Shared by the three steps that can open a trace — the run in time, the
 * junction, and the piece — because the object being read is the same object
 * in all three, and a second copy of this band would be a second wording of
 * « ce que tu as joué » for the same lane.
 */

import { PANEL_PADDING_X_PX } from "./panel-metrics";

export function TraceBand({
  stopBar,
  hint,
  onClose,
}: {
  /** The bar the pass came off at, when it did. Named, never counted. */
  readonly stopBar: number | null;
  /**
   * What to do with the roll, or nothing.
   *
   * A whole piece is about 3% visible at a time and has to be scrolled; a
   * unit of four bars is mostly on screen already, and telling that player to
   * scroll would send them looking for something that is under their eyes.
   */
  readonly hint: string | null;
  readonly onClose: () => void;
}) {
  return (
    <div style={{ padding: `10px ${PANEL_PADDING_X_PX}px 12px` }}>
      <div className="flex items-center" style={{ gap: 14 }}>
        <span
          className="min-w-0 flex-1 truncate"
          style={{ fontSize: 12, color: "var(--color-text-primary)" }}
        >
          {stopBar === null
            ? "Ce que tu as joué."
            : `Ce que tu as joué. Mesure ${stopBar}.`}
        </span>

        <button
          type="button"
          onClick={onClose}
          className="shrink-0 transition-colors hover:bg-white/[0.10] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
          style={{
            padding: "7px 12px",
            borderRadius: 8,
            border: "1px solid var(--color-border-subtle)",
            background: "rgba(255, 255, 255, 0.06)",
            color: "var(--color-text-primary)",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          Revenir au morceau
        </button>
      </div>

      {hint !== null && (
        <p
          style={{
            margin: "10px 0 0",
            fontSize: 11,
            color: "var(--color-text-secondary)",
          }}
        >
          {hint}
        </p>
      )}
    </div>
  );
}
